import { useState } from "react";
import axios, { AxiosError } from "axios";
import {
	BulkOrderOperationError,
	BulkOrderOperationRequest,
	BulkOrderOperationResponse,
	executeBulkOrderOperation,
} from "../api/bulk-order-operations";

/**
 * Submit state for one bulk operation run, shared by every category screen.
 *
 * Business-rule failures come back as HTTP 400 with a BulkOrderOperationErrorDto
 * body and are rendered inline. Body-binding failures return framework
 * ProblemDetails instead (no `message`), and network/5xx errors have no body at
 * all — those are left to the axios interceptor's toast, since there is nothing
 * order-specific to show.
 */
const useBulkOrderOperation = () => {
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState<BulkOrderOperationResponse | null>(
		null
	);
	const [errorResult, setErrorResult] =
		useState<BulkOrderOperationError | null>(null);

	const clearResults = () => {
		setResult(null);
		setErrorResult(null);
	};

	/**
	 * Submit anything that returns the shared response shape. Categories that do not
	 * go through `execute` (Section ID is driverless, so it has its own route) reuse
	 * the same submit/error/result handling by passing their own call here.
	 */
	const run = async (send: () => Promise<BulkOrderOperationResponse>) => {
		setSubmitting(true);
		clearResults();
		try {
			setResult(await send());
		} catch (err) {
			if (axios.isAxiosError(err)) {
				const axiosErr = err as AxiosError;
				const data = axiosErr.response?.data as
					| Partial<BulkOrderOperationError>
					| undefined;
				if (
					axiosErr.response?.status === 400 &&
					data &&
					typeof data.message === "string"
				) {
					setErrorResult({
						message: data.message,
						notFoundOrderIds: data.notFoundOrderIds ?? [],
						notDeliveredOrderIds: data.notDeliveredOrderIds ?? [],
						missingUuidOrderIds: data.missingUuidOrderIds ?? [],
						notFoundInDestinationOrderIds:
							data.notFoundInDestinationOrderIds ?? [],
						deliveredWithBalanceOrderIds:
							data.deliveredWithBalanceOrderIds ?? [],
					});
				}
			}
		} finally {
			setSubmitting(false);
		}
	};

	const execute = (body: BulkOrderOperationRequest) =>
		run(() => executeBulkOrderOperation(body));

	return { submitting, result, errorResult, execute, run, clearResults };
};

export default useBulkOrderOperation;
